<template>
  <div>
    <div v-if="!isCaseResolved">
      <validation-observer tag="form" ref="documentManagementRef" novalidate>
        <div class="row p-4">
          <div class="col-12">
            <validation-provider rules="required" v-slot="{ errors }">
              <div class="color-gray-18 font-14 mb-1">{{ $t('DocType') }}:</div>
              <custom-drop-down
                :placeholder="$i18n.t('Item-Empty')"
                :options="categories"
                fieldkey="id"
                fieldname="name"
                class="case-info__dropdown"
                v-model="model.category"
                :error="errors[0]"
                :custom-error-msg="`${$i18n.t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-12 mt-3">
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-file-input
                v-model="model.document"
                :accept="allowedExtensions"
                :on-download="onDownloadFile"
                :error="errors[0]"
                :custom-error-msg="`${$i18n.t('RequiredField')}.`"
                ref="documentRef"
                :file-extension-error-msg="customFileExtensionErrorMsg"
                display-style="normal"
                class="file-input__extended"
              />
            </validation-provider>
          </div>
          <div class="col-12 mt-3">
            <checkbox
              :text="$t('IsPublicQuestion')"
              class=""
              v-model="model.isPublic"
            />
          </div>
          <div class="col-12 text-right mt-3">
            <action-button
              :text="$t('btCancel.Text')"
              class="warning outline-warning px-4 py-2 mr-2"
              i="fal fa-times-circle"
              @click="onCancelHandler"
            />
            <action-button
              :text="$t('Save')"
              class="btn-success px-4 py-2"
              i="fal fa-check-circle"
              @click="onAddHandler"
            />
          </div>
        </div>
      </validation-observer>
    </div>
    <separator-extended
      :text="$t('lblAttachments')"
      class="separator__extended"
    />
    <div class="row px-3 pt-2">
      <div class="col">
        <div
          v-for="(item, index) in attachments"
          :key="`doc-item-${index}`"
          class="border-bottom mb-3 pb-2"
        >
          <div class="d-flex align-items-center">
            <div class="flex-grow-1">
              <div class="color-black font-16 font-weight-bold">
                {{ item.attachmentCategoryName }}
              </div>
              <span class="color-gray-13 font-14">{{ item.fileName }}</span>
            </div>
            <div class="d-flex">
              <action-button
                class="outline-success px-1 pl-2 pr-0 d-flex align-items-center justify-content-center"
                i="fa fa-file-download"
                @click="onDownloadHandler(item.itemAttachmentId)"
              />
              <action-button
                v-if="!isCaseResolved"
                class="outline-danger btn-cancel danger px-0 pl-2 ml-2"
                i="fa fa-times-circle"
                @click="onDeleteHandler(item.itemAttachmentId)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import ActionButton from 'ActionButton';
import Checkbox from 'CheckBox';
import { SeparatorExtended } from '@/components/common';
import {
  initAttachmentCatalog as _initAttachmentCatalog,
  saveItemAttachment as _saveItemAttachment,
  downloadItemAttachment as _downloadItemAttachment,
  getItemAttachments as _getItemAttachments,
  removeItemAttachment as _removeItemAttachment,
} from '@/modules/tickets/api.js';
import alertMixin from 'utils/mixins/alert.mixin';
import { i18n } from '@/i18n';

export default {
  name: 'DocumentManagement',
  mixins: [alertMixin],
  components: {
    CustomDropDown,
    CustomFileInput,
    ActionButton,
    SeparatorExtended,
    Checkbox,
  },
  data() {
    return {
      model: {
        category: null,
        document: null,
        isPublic: false,
      },
      categories: [],
      attachments: [],
      allowedExtensions: '',
    };
  },
  computed: {
    businessId() {
      return this.itemModel?.requestNumber;
    },
    itemId() {
      return this.itemModel?.itemId;
    },
    customFileExtensionErrorMsg() {
      return i18n.t('InvalidFormatMessage');
    },
  },
  async mounted() {
    await this.callGetCategories();
    if (this.businessId) {
      await this.callGetItemAttachments(this.businessId);
    }
    this.resetFileInput();
  },
  methods: {
    async callGetCategories() {
      const { data } = await _initAttachmentCatalog();
      this.categories = data.categories;
      this.allowedExtensions = data.fileTypes;
    },
    async callGetItemAttachments(businessId) {
      const { data } = await _getItemAttachments(businessId);
      this.attachments = data;
    },
    onCancelHandler() {
      this.setIsNavOpen({
        isNavOpen: false,
        navOption: {},
      });
    },
    async onAddHandler() {
      const isValid = await this.$refs.documentManagementRef.validate();
      if (!isValid || this.isUserEmpty) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }
      const filename = this.model.document?.name;
      const payload = {
        itemId: this.itemId,
        fileExtension: filename
          .substring(filename.lastIndexOf('.') + 1)
          .toLowerCase(),
        categoryId: this.model.category?.id,
        fileName: this.model.document?.name,
        isPublic: this.model.isPublic ?? false,
        file: this.model.document,
      };

      let formData = objectToFormData(payload, {
        indices: true,
        nullsAsUndefineds: true,
      });

      await _saveItemAttachment(formData);

      this.ShowToast(
        i18n.t('ProcessSuccessfully'),
        i18n.t('DocumentHasBeenAddedSuccessfullyMessage'),
        'success'
      );

      this.model = {};
      this.$refs.documentManagementRef.reset();
      this.resetFileInput();
      await this.callGetItemAttachments(this.businessId);
    },
    async onDownloadHandler(itemAttachmentId) {
      await _downloadItemAttachment(itemAttachmentId);
    },
    async onDeleteHandler(itemAttachmentId) {
      if (itemAttachmentId) {
        const answer = await this.ShowCancelModal(
          i18n.t('RemoveDocument'),
          i18n.t('AreYouSureWantToRemoveDocumentMessage'),
          i18n.t('modal-yes-text'),
          i18n.t('modal-no-text')
        );
        if (answer) {
          await _removeItemAttachment(itemAttachmentId);
          this.ShowToast(
            i18n.t('ProcessSuccessfully'),
            i18n.t('DocumentSuccessfullyRemovedMessage'),
            'success'
          );
          await this.callGetItemAttachments(this.businessId);
        }
      }
    },
    resetFileInput() {
      this.$refs.documentRef.removeFile();
    },
    onDownloadFile() {},
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/bootstrap/_colors.scss';
@import '@/scss/components/action-button/index.scss';

.file-input__extended ::v-deep {
  .file-input-container {
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 1px solid map-get($colors, 'orange-light-2');
    border-radius: 4px;
    .file-select {
      em {
        color: map-get($colors, 'orange-light-2') !important;
      }
      em.fa-file-plus {
        &:before {
          content: '\f0ee' !important;
        }
      }
      span {
        color: map-get($colors, 'orange-light-2') !important;
        font-weight: normal !important;
      }
    }
  }
}
::v-deep .separator__extended {
  .separator-custom {
    .separator-custom__text {
      color: map-get($colors, 'dark-blue-3') !important;
      margin: 0 5px;
    }
  }
}
</style>
